import {
  ClipboardDocumentCheckIcon,
  FolderIcon,
  InformationCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/solid';
import { isAxiosError } from 'axios';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Dropdown } from '@/components/Dropdown';
import { Date as DateInput } from '@/components/form/Date';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useAxios } from '@/hooks/useAxios';
import { useViewedTenant } from '@/hooks/useViewedTenant';
import { queryClient } from '@/queryclient';
import { formatStatus } from '@/sections/attacks/redTeam/domain-parking/utils/parkedDomainFormatting';
import { ParkedDomain } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

const ToggleSwitch = ({
  checked,
  disabled,
  onChange,
  id,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  id: string;
}) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out',
        'focus:ring-chariot-brand-primary focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
        disabled ? 'opacity-50' : 'cursor-pointer',
        checked ? 'bg-chariot-brand-primary' : 'bg-chariot-text-disabled'
      )}
      data-testid={`toggle-${id}`}
    >
      <span
        className={cn(
          'inline-block h-3 w-3 transform rounded-full transition-transform duration-200 ease-in-out',
          'bg-white shadow-xs',
          checked ? 'translate-x-5' : 'translate-x-1'
        )}
      />
    </button>
  );
};

interface ParkedDomainActionsProps {
  parkedDomain: ParkedDomain;
  className?: string;
  onUpdate?: () => void | Promise<unknown>;
  /**
   * Callback for actions that affect Mailgun (burn, archive).
   * Called with a 3-second delay to allow Mailgun to update.
   */
  onMailgunUpdate?: () => void | Promise<unknown>;
  /**
   * Callback for when DNS records should be refreshed (e.g., after unparking)
   */
  onDnsUpdate?: () => void | Promise<unknown>;
  /**
   * Callback when a parking job is initiated with the target category
   */
  onParkingInitiated?: (category: string) => void;
  autoRenew: boolean;
  isTogglingAutoRenew: boolean;
  onAutoRenewToggle: (newValue: boolean) => void;
  /**
   * Whether a parking job is currently active (queued or running)
   */
  isParkingJobActive?: boolean;
}

/**
 * Convert ISO date string to YYYY-MM-DD format for date input
 */
function toDateInputFormat(isoString: string | undefined | null): string {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
}

/**
 * Body shape for POST /red-team/domain-parking when transitioning to in-use.
 * Backend fills tenant_name server-side from aws.Username — do NOT send it.
 */
interface CheckoutPayload {
  key: string;
  status: 'in-use';
  checkout_start: string;
  checkout_end: string;
  comment: string;
  engagement_type: string;
  override?: boolean;
}

/**
 * ParkedDomainActions
 *
 * Action buttons for parked domain management with dropdowns for:
 * - Parked Category: Unpark, Finance, Health, Legal, Business
 * - Managed: Mark available, Mark in-use, Burn, Reserve, Archive
 */
export function ParkedDomainActions({
  parkedDomain,
  className,
  onUpdate,
  onMailgunUpdate,
  onDnsUpdate,
  onParkingInitiated,
  autoRenew,
  isTogglingAutoRenew,
  onAutoRenewToggle,
  isParkingJobActive = false,
}: ParkedDomainActionsProps) {
  const axios = useAxios();
  const { email: currentTenantEmail, displayName: currentTenantDisplayName } =
    useViewedTenant();
  const [showAutoRenewExpiredTooltip, setShowAutoRenewExpiredTooltip] =
    useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Check if domain has expired
  const isExpired = parkedDomain.expires
    ? new Date(parkedDomain.expires) < new Date()
    : false;

  // Disable parked category dropdown when parking job is active or domain expired
  const isCategoryDisabled = isExpired || isParkingJobActive;

  // Dialog state for Checking out a domain
  const [isInUseDialogOpen, setIsInUseDialogOpen] = useState(false);
  const [inUseFormData, setInUseFormData] = useState({
    checkoutStart: '',
    checkoutEnd: '',
    comment: '',
    engagementType: 'redteam',
  });

  // Dialog state for adding a history note
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  // 409 override confirmation state — captures the reason from backend so
  // operator can decide whether to force the claim via override=true.
  const [overrideConfirmation, setOverrideConfirmation] = useState<{
    reason: string;
    payload: CheckoutPayload;
  } | null>(null);

  // Parked Category dropdown options
  const categoryOptions: DropdownOption[] = [
    {
      id: 'unpark',
      label: 'Unpark',
      value: 'unpark',
      onClick: () => handleCategoryChange('unpark'),
    },
    {
      id: 'finance',
      label: 'Finance',
      value: 'finance',
      onClick: () => handleCategoryChange('finance'),
    },
    {
      id: 'health',
      label: 'Health',
      value: 'health',
      onClick: () => handleCategoryChange('health'),
    },
    {
      id: 'legal',
      label: 'Legal',
      value: 'legal',
      onClick: () => handleCategoryChange('legal'),
    },
    {
      id: 'business',
      label: 'Business',
      value: 'business',
      onClick: () => handleCategoryChange('business'),
    },
  ];

  // Managed dropdown options
  const managedOptions: DropdownOption[] = [
    {
      id: 'available',
      label: 'Mark As Available',
      value: 'available',
      onClick: () => handleStatusChange('available'),
    },
    {
      id: 'in-use',
      label: 'Checkout',
      value: 'in-use',
      onClick: () => {
        // Open dialog and prefill with existing data if available
        setInUseFormData({
          checkoutStart: toDateInputFormat(parkedDomain.checkout_start),
          checkoutEnd: toDateInputFormat(parkedDomain.checkout_end),
          comment: '',
          engagementType: 'redteam',
        });
        setIsInUseDialogOpen(true);
      },
    },
    {
      id: 'burn',
      label: 'Burn',
      value: 'burn',
      onClick: () => handleStatusChange('burned'),
    },
    {
      id: 'reserve',
      label: 'Reserve',
      value: 'reserve',
      onClick: () => handleStatusChange('reserved'),
    },
    {
      id: 'archive',
      label: 'Archive',
      value: 'archive',
      onClick: () => handleStatusChange('archived'),
    },
  ];

  // Handler for category change
  const handleCategoryChange = async (category: string) => {
    // Map UI labels to backend values
    const categoryMap: Record<string, string> = {
      unpark: 'none',
      legal: 'legal',
      business: 'business',
      finance: 'finance',
      health: 'health',
    };

    const backendCategory = categoryMap[category];

    try {
      await axios.put('/red-team/domain-parking', {
        key: parkedDomain.key,
        parked_category: backendCategory,
      });

      // Show toast immediately after PUT succeeds, before query invalidations
      // This ensures user sees "Initiated" before any job status toasts
      if (category === 'unpark') {
        toast.success('Successfully unparked');
        // Refresh DNS records after unparking (synchronous operation)
        await onDnsUpdate?.();
      } else {
        toast.success(`Initiated parking to ${category}`);
        // Notify parent that a parking job was initiated with this category
        onParkingInitiated?.(category);
      }

      // Invalidate parking job query to start polling the new job
      await queryClient.invalidateQueries({
        queryKey: ['parking-job', parkedDomain.domain],
      });

      // Refresh drawer data to show updated information
      // Note: We don't invalidate all parked domain queries here to avoid race conditions
      // where background refetches could overwrite fresh data. The onUpdate callback
      // handles refreshing the drawer data, and the table will refresh via handleParkingJobSuccess.
      await onUpdate?.();
    } catch (error: unknown) {
      // Extract error message from backend response
      let errorMessage = 'Failed to update category';
      if (isAxiosError(error)) {
        if (error.response?.data) {
          // Backend may send a raw string or an object with message/error properties
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else {
            errorMessage =
              error.response.data.message ||
              error.response.data.error ||
              errorMessage;
          }
        }
      }
      toast.error(errorMessage);
    }
  };

  // Handler for managed status change
  const handleStatusChange = async (
    status: string,
    additionalData?: {
      checkoutStart?: string;
      checkoutEnd?: string;
    }
  ) => {
    setIsUpdatingStatus(true);
    try {
      const payload: {
        key: string;
        status: string;
        checkout_start?: string;
        checkout_end?: string;
      } = {
        key: parkedDomain.key,
        status: status,
      };

      // Add additional data for checkout
      if (additionalData) {
        if (additionalData.checkoutStart) {
          payload.checkout_start = additionalData.checkoutStart;
        }
        if (additionalData.checkoutEnd) {
          payload.checkout_end = additionalData.checkoutEnd;
        }
      }

      await axios.put('/red-team/domain-parking', payload);

      // Invalidate parked domains cache to refresh the list
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'parkeddomain';
        },
        refetchType: 'active',
      });

      // Refresh drawer data to show updated information
      await onUpdate?.();

      toast.success(`Usage status updated to: ${formatStatus(status)}`);

      // For actions that affect Mailgun (burn, archive), refresh Mailgun and DNS sections
      // Mailgun takes a few seconds to update after backend deletes the domain
      if (status === 'burned' || status === 'archived') {
        onMailgunUpdate?.();
        onDnsUpdate?.();
      }
    } catch (error: unknown) {
      console.error('Failed to update usage status:', error);
      // Extract error message from backend response
      let errorMessage = 'Failed to update usage status';
      if (isAxiosError(error)) {
        if (error.response?.data) {
          // Backend may send a raw string or an object with message/error properties
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else {
            errorMessage =
              error.response.data.message ||
              error.response.data.error ||
              errorMessage;
          }
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Submit a checkout POST — either the initial request or an override retry.
  // Backend rejects tenant_name in the body; tenant is server-set from aws.Username.
  const submitCheckoutPost = async (payload: CheckoutPayload) => {
    setIsUpdatingStatus(true);
    try {
      await axios.post('/red-team/domain-parking', payload);

      // Invalidate parked domains cache to refresh the list
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'parkeddomain';
        },
        refetchType: 'active',
      });

      // Refresh drawer data to show updated information
      await onUpdate?.();

      toast.success('Domain checked out');

      // Close dialog and reset form
      setIsInUseDialogOpen(false);
      setOverrideConfirmation(null);
      setInUseFormData({
        checkoutStart: '',
        checkoutEnd: '',
        comment: '',
        engagementType: 'redteam',
      });
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 409) {
        // Reuse-window conflict — surface reason and let operator override.
        const data = error.response.data;
        let reason = 'This domain was recently claimed by this tenant.';
        if (typeof data === 'string') {
          reason = data;
        } else if (data && typeof data === 'object') {
          reason = data.error || data.message || reason;
        }
        setOverrideConfirmation({ reason, payload });
      } else {
        console.error('Failed to check out domain:', error);
        let errorMessage = 'Failed to check out domain';
        if (isAxiosError(error)) {
          if (error.response?.data) {
            if (typeof error.response.data === 'string') {
              errorMessage = error.response.data;
            } else {
              errorMessage =
                error.response.data.message ||
                error.response.data.error ||
                errorMessage;
            }
          }
        }
        toast.error(errorMessage);
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Handler for submitting the checkout form
  const handleInUseSubmit = async () => {
    // Validate required fields
    if (!inUseFormData.checkoutStart || !inUseFormData.checkoutEnd) {
      toast.error('Start date and end date are required');
      return;
    }

    // Validate end date is after start date
    if (
      new Date(inUseFormData.checkoutEnd) <
      new Date(inUseFormData.checkoutStart)
    ) {
      toast.error('End date must be after start date');
      return;
    }

    // Format dates in UTC - end date should be inclusive (11:59:59 PM UTC)
    const checkoutStartFormatted = new Date(
      inUseFormData.checkoutStart + 'T00:00:00Z'
    ).toISOString();
    const checkoutEndFormatted = new Date(
      inUseFormData.checkoutEnd + 'T23:59:59Z'
    ).toISOString();

    const payload: CheckoutPayload = {
      key: parkedDomain.key,
      status: 'in-use',
      checkout_start: checkoutStartFormatted,
      checkout_end: checkoutEndFormatted,
      comment: inUseFormData.comment,
      engagement_type: inUseFormData.engagementType,
      // NO tenant_name — backend fills from aws.Username
    };

    await submitCheckoutPost(payload);
  };

  const handleOverrideConfirm = async () => {
    if (!overrideConfirmation) return;
    await submitCheckoutPost({
      ...overrideConfirmation.payload,
      override: true,
    });
  };

  // Handler for submitting the Add Note dialog. Sends only { key, comment }
  // to the parked-domain PUT endpoint — no status change. Backend appends a
  // history record with the comment attached.
  const handleAddNoteSubmit = async () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;

    setIsSubmittingNote(true);
    try {
      await axios.put('/red-team/domain-parking', {
        key: parkedDomain.key,
        comment: trimmed,
      });

      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'parkeddomain';
        },
        refetchType: 'active',
      });

      await onUpdate?.();

      toast.success('Note added');
      setIsNoteDialogOpen(false);
      setNoteText('');
    } catch (error: unknown) {
      let errorMessage = 'Failed to add note';
      if (isAxiosError(error)) {
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else {
            errorMessage =
              error.response.data.message ||
              error.response.data.error ||
              errorMessage;
          }
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  // Tooltip text for parking job
  const parkingTooltipText =
    'Launching Netlify website. This can take between 20 seconds to a few minutes.';

  return (
    <>
      <div className={cn('flex items-center gap-2', className)}>
        {/* Parked Category Dropdown with optional spinner */}
        <Tooltip
          title={
            isParkingJobActive
              ? parkingTooltipText
              : isExpired
                ? 'Domain has expired'
                : ''
          }
          placement="bottom"
        >
          <div className="inline-block">
            <Dropdown
              title={isCategoryDisabled ? undefined : 'Parked Category Options'}
              size="sm"
              options={categoryOptions}
              placeholder="Update Category"
              styleType="rounded"
              border={false}
              icon={
                isParkingJobActive ? (
                  <Spinner
                    size="sm"
                    className="text-chariot-text-primary"
                    data-testid="parking-job-spinner"
                  />
                ) : (
                  <FolderIcon className="size-4" />
                )
              }
              className={cn(
                '[&_span:nth-child(3)]:hidden',
                isCategoryDisabled && 'pointer-events-none',
                isExpired && 'opacity-50' // Only dim when expired, not when parking
              )}
              listClassName="!z-[150]"
            />
          </div>
        </Tooltip>

        {/* Managed Dropdown */}
        <Tooltip
          title={
            isUpdatingStatus
              ? 'Updating usage status...'
              : isExpired
                ? 'Domain has expired'
                : ''
          }
          placement="bottom"
        >
          <div className="inline-block">
            <Dropdown
              title={
                isUpdatingStatus || isExpired
                  ? undefined
                  : 'Usage Status Options'
              }
              size="sm"
              options={managedOptions}
              placeholder="Update Status"
              styleType="rounded"
              border={false}
              icon={
                isUpdatingStatus ? (
                  <Spinner
                    size="sm"
                    className="text-chariot-text-primary"
                    data-testid="status-update-spinner"
                  />
                ) : (
                  <ClipboardDocumentCheckIcon className="size-4" />
                )
              }
              className={cn(
                '[&_span:nth-child(3)]:hidden',
                (isUpdatingStatus || isExpired) && 'pointer-events-none',
                isExpired && 'opacity-50' // Only dim when expired, not when updating
              )}
              listClassName="!z-[150]"
            />
          </div>
        </Tooltip>

        {/* Add Note Button — always available, appends a history record */}
        <Tooltip title="Add a note to the history timeline" placement="bottom">
          <button
            type="button"
            onClick={() => setIsNoteDialogOpen(true)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
              'text-chariot-text-primary hover:bg-chariot-background-tertiary',
              'focus:ring-chariot-brand-primary transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden'
            )}
            data-testid="add-note-button"
          >
            <PencilSquareIcon className="size-4" />
            Add note
          </button>
        </Tooltip>

        {/* Auto Renew Toggle */}
        <div
          className="relative inline-flex items-center gap-2"
          onMouseEnter={() => isExpired && setShowAutoRenewExpiredTooltip(true)}
          onMouseLeave={() => setShowAutoRenewExpiredTooltip(false)}
        >
          <span className="text-chariot-text-secondary text-xs font-medium">
            Auto Renew
          </span>
          <ToggleSwitch
            id="auto-renew-actions"
            checked={autoRenew}
            disabled={isTogglingAutoRenew || isExpired}
            onChange={onAutoRenewToggle}
          />
          {isExpired && showAutoRenewExpiredTooltip && (
            <div className="bg-chariot-background-primary border-chariot-stroke absolute top-full left-0 z-50 mt-1 w-max rounded-md border px-3 py-2 shadow-lg">
              <p className="text-chariot-text-secondary text-xs whitespace-nowrap">
                Domain has expired
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mark as In-Use Dialog */}
      <Modal
        open={isInUseDialogOpen}
        onClose={() => {
          setIsInUseDialogOpen(false);
          setInUseFormData({
            checkoutStart: '',
            checkoutEnd: '',
            comment: '',
            engagementType: 'redteam',
          });
        }}
        title="Checkout the Domain"
        subtitle="Provide additional details"
        size="lg"
        // Modal auto-focuses the primary footer button 100ms after open; that steals
        // focus mid-type in the comment textarea (visible under CI test load, also a UX papercut).
        disableAutoFocus
        footer={{
          text: 'Submit',
          onClick: handleInUseSubmit,
          disabled: isUpdatingStatus,
        }}
      >
        <div className="flex flex-col gap-4 py-4">
          {/* Reserving as tenant (display-only — backend derives tenant from session) */}
          <div
            className="text-chariot-text-muted text-sm"
            data-testid="reserving-as-tenant"
          >
            Reserving as{' '}
            <span className="text-chariot-text-primary font-medium">
              {currentTenantDisplayName}
            </span>
          </div>

          {/* Start Date */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="checkoutStart"
              className="text-chariot-text-primary flex items-center gap-1.5 text-sm font-medium"
            >
              Start Date <span className="text-red-500">*</span>
              <InformationCircleIcon
                className="text-chariot-text-secondary size-4"
                title="Start date in UTC"
              />
            </label>
            <DateInput
              name="checkoutStart"
              value={inUseFormData.checkoutStart}
              onChange={e =>
                setInUseFormData(prev => ({
                  ...prev,
                  checkoutStart: e.target.value,
                }))
              }
              required
            />
          </div>

          {/* End Date */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="checkoutEnd"
              className="text-chariot-text-primary flex items-center gap-1.5 text-sm font-medium"
            >
              End Date <span className="text-red-500">*</span>
              <InformationCircleIcon
                className="text-chariot-text-secondary size-4"
                title="End date in UTC, inclusive"
              />
            </label>
            <DateInput
              name="checkoutEnd"
              value={inUseFormData.checkoutEnd}
              onChange={e =>
                setInUseFormData(prev => ({
                  ...prev,
                  checkoutEnd: e.target.value,
                }))
              }
              required
              min={inUseFormData.checkoutStart || undefined}
            />
          </div>

          {/* Comment (persists into history record) */}
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="checkoutComment"
              className="text-chariot-text-primary text-sm font-medium"
            >
              Comment
            </Label>
            <Textarea
              id="checkoutComment"
              name="checkoutComment"
              value={inUseFormData.comment}
              onChange={e =>
                setInUseFormData(prev => ({
                  ...prev,
                  comment: e.target.value,
                }))
              }
              rows={3}
              placeholder="Optional context for this checkout"
            />
          </div>

          {/* Engagement Type (wire value stays lowercase; only display label is titled) */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="engagementType"
              className="text-chariot-text-primary text-sm font-medium"
            >
              Engagement Type
            </label>
            <Select
              value={inUseFormData.engagementType}
              onValueChange={value =>
                setInUseFormData(prev => ({
                  ...prev,
                  engagementType: value,
                }))
              }
            >
              <SelectTrigger id="engagementType" aria-label="Engagement Type">
                <SelectValue placeholder="Select engagement type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="redteam">Red Team</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Modal>

      {/* 409 Override Confirmation Modal */}
      <Modal
        open={overrideConfirmation !== null}
        onClose={() => setOverrideConfirmation(null)}
        title="Override tenant reuse guard?"
        size="xs"
        baseClassName="max-w-[440px]"
        footer={{
          text: 'Override',
          onClick: handleOverrideConfirm,
          disabled: isUpdatingStatus,
        }}
      >
        {overrideConfirmation && (
          <p className="text-chariot-text-secondary text-sm">
            This domain isn&apos;t eligible for claim by this tenant:{' '}
            {currentTenantEmail &&
            currentTenantDisplayName &&
            currentTenantEmail !== currentTenantDisplayName
              ? overrideConfirmation.reason.replaceAll(
                  currentTenantEmail,
                  currentTenantDisplayName
                )
              : overrideConfirmation.reason}
            . Override anyway?
          </p>
        )}
      </Modal>

      {/* Add Note Modal — sends only { key, comment } to append a history record */}
      <Modal
        open={isNoteDialogOpen}
        onClose={() => {
          setIsNoteDialogOpen(false);
          setNoteText('');
        }}
        title="Add note"
        size="lg"
        // Modal auto-focuses the primary footer button 100ms after open; that steals
        // focus mid-type in the note textarea (fixes CI test flake and a UX papercut).
        disableAutoFocus
        footer={{
          text: isSubmittingNote ? 'Submitting...' : 'Submit',
          onClick: handleAddNoteSubmit,
          disabled: isSubmittingNote || noteText.trim() === '',
          'data-testid': 'add-note-submit',
        }}
      >
        <div className="flex flex-col gap-2 py-4">
          <Label htmlFor="parked-domain-note" className="sr-only">
            Note
          </Label>
          <Textarea
            id="parked-domain-note"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Add a note to the history timeline"
            rows={4}
            data-testid="add-note-textarea"
          />
        </div>
      </Modal>
    </>
  );
}
